import { memo, useEffect, useRef } from 'react'

import { FormControlLabel, Switch } from '@mui/material'
import { SmepEventConfig, SmepWorkspaceDefaultEventConfig } from '@nativewaves/nw-cloud-sdk/smep'
import { useQuery } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Editor, EditorInstance } from '@shared/components/Editor'
import { LoadingIndicator } from '@shared/layouts'
import { useWorkspaceUpdateMutation } from 'hooks/mutations/smep'
import { PageError } from 'layouts'
import { workspaceSettingsRoute } from 'pages/NWPlatform/Workspaces/Workspace/Settings/Routing'
import { workspaceQueryKeys } from 'services/queryKeys'

const DefaultEventConfigRaw = () => {
  const { t } = useTranslation(['common', 'domain'])
  const { enqueueSnackbar } = useSnackbar()

  const editorInstanceRef = useRef<EditorInstance>(undefined)

  const { workspaceId } = workspaceSettingsRoute.useParams()

  const workspaceQuery = useQuery(workspaceQueryKeys.detail({ workspaceId }))

  const { mutate, isPending } = useWorkspaceUpdateMutation()

  // Keep the editor in sync with server truth (e.g. after a save).
  useEffect(() => {
    if (editorInstanceRef.current && workspaceQuery.isSuccess) {
      editorInstanceRef.current
        .getModel()
        ?.setValue(window.JSON.stringify(workspaceQuery.data.defaultEventConfig?.config ?? {}, null, 4))
    }
  }, [workspaceQuery])

  if (workspaceQuery.isLoading) {
    return <LoadingIndicator />
  }

  if (!workspaceQuery.isSuccess) {
    return <PageError />
  }

  const defaultEventConfig = workspaceQuery.data.defaultEventConfig
  const enabled = defaultEventConfig?.enabled ?? false
  const config = defaultEventConfig?.config ?? undefined

  const save = (next: SmepWorkspaceDefaultEventConfig, message: string) =>
    mutate(
      { workspaceId, smepWorkspaceEdit: { defaultEventConfig: next } },
      { onSuccess: () => enqueueSnackbar(message, { variant: 'success' }) },
    )

  return (
    <Editor
      value={config ?? ({} as SmepEventConfig)}
      inProgress={isPending}
      title={t('domain:Workspace.Settings.DefaultEventConfig.title')}
      onSubmit={(data: SmepEventConfig) =>
        save({ enabled, config: data }, t('domain:Workspace.Settings.DefaultEventConfig.saved'))
      }
      getEditorInstance={(editorInstance: EditorInstance) => void (editorInstanceRef.current = editorInstance)}
      CustomActionsSlot={
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={enabled}
              disabled={isPending}
              onChange={(_, value) =>
                save(
                  { enabled: value, config },
                  t(
                    value
                      ? 'domain:Workspace.Settings.DefaultEventConfig.enabledMessage'
                      : 'domain:Workspace.Settings.DefaultEventConfig.disabledMessage',
                  ),
                )
              }
            />
          }
          label={t('domain:Workspace.Settings.DefaultEventConfig.enabled')}
        />
      }
    />
  )
}

export const DefaultEventConfig = memo(DefaultEventConfigRaw)
